"use client";

import React from "react";
import { Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function NewsletterCallToAction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="22332" id="stay-updated" ref={ref} className="relative w-full overflow-hidden bg-muted py-48 transition-opacity duration-1000 opacity-100 !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607942/img/ChatGPT-Image-Apr-19-2026-09_05_58-AM-1.png)]">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute inset-0 h-full w-full" style={{
        backgroundImage: "linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)",
        backgroundSize: "40px 40px"
      }} />
      </div>

      <div className="container relative z-10 mx-auto px-6">
        <div className="mx-auto max-w-3xl text-center p-8 md:p-10 !bg-[hsl(0,0%,96%)] !rounded-xl !shadow-[0px_36px_72px_rgba(20,21,31,0.5),_0px_18px_36px_rgba(20,21,31,0.30)] !text-[hsl(137,66%,31%)] !border-[hsl(0,0%,0%)] border-solid !border-4">
          <h2 className="mb-16 text-5xl tracking-tighter !text-[hsl(137,66%,31%)] !font-sans !font-bold md:text-7xl">STAY UPDATED</h2>

          <div className="mx-auto w-full md:w-2/3 lg:w-1/2">
            <FormProvider formKey="stay_updated_form_blog" formId="stay_updated_form_blog" sectionName="newsletter-cta" defaultValues={{
            first_name: "",
            email: "",
            language: "English",
            interests: []
          }} validationRules={{
            first_name: {
              required: false
            },
            email: {
              required: true,
              pattern: "email"
            },
            language: {
              required: true
            },
            interests: {
              required: false
            }
          }} submitText="SUBSCRIBE" successMessage="You have been added to our maintenance alert list." errorMessage="Unable to subscribe. Please try again or call us directly.">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border bg-background/50 p-8 text-center backdrop-blur-sm rounded-xl">
        <p className="font-sans text-lg text-foreground font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 text-left">
      <div className="space-y-8 pb-6">
        {}
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem className="space-y-1 py-4">
              <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                First Name
              </FormLabel>
              <FormControl>
                <Input placeholder="STEVE" {...field} className="h-12 rounded-none border-0 border-b border-border bg-transparent px-0 font-sans text-lg text-foreground placeholder:text-muted-foreground/40 focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-4px_rgba(35,115,55,0.3)] transition-all duration-300" />
              </FormControl>
              <FormMessage className="font-sans text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1 py-4">
              <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Email Address <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="STEVE@EXAMPLE.COM" {...field} className="h-12 rounded-none border-0 border-b border-border bg-transparent px-0 font-sans text-lg text-foreground placeholder:text-muted-foreground/40 focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-4px_rgba(35,115,55,0.3)] transition-all duration-300" />
              </FormControl>
              <FormMessage className="font-sans text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="language" render={({
        field
      }) => <FormItem className="space-y-1 py-4">
              <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Preferred Language <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className="w-full appearance-none rounded-none border-0 border-b border-border bg-transparent py-3 px-0 font-sans text-lg text-foreground focus:border-primary focus:outline-none focus:shadow-[0_4px_12px_-4px_rgba(35,115,55,0.3)] transition-all duration-300">
                    <option value="English" className="bg-background text-foreground">
                      English
                    </option>
                    <option value="Spanish" className="bg-background text-foreground">
                      Spanish
                    </option>
                  </select>
                  <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage className="font-sans text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="interests" render={({
        field
      }) => <FormItem className="space-y-4 pt-8 pb-6">
              <FormLabel className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Services of Interest
              </FormLabel>
              <div className="grid gap-4 sm:grid-cols-1">
                {["Repair Tips", "New Installations", "Safety Inspections"].map((option, i) => <div key={option} data-index={i} className="flex items-center space-x-3">
                      <FormControl>
                        <input type="checkbox" value={option} checked={field.value?.includes(option)} onChange={e => {
                const checked = e.target.checked;
                const current = field.value || [];
                const updated = checked ? [...current, option] : current.filter((val: string) => val !== option);
                field.onChange(updated);
              }} className="size-4 appearance-none border border-border bg-background checked:bg-primary checked:border-primary focus:ring-1 focus:ring-primary focus:ring-offset-0 focus:outline-none transition-colors cursor-pointer rounded-sm" />
                      </FormControl>
                      <span className="font-sans text-sm text-foreground uppercase tracking-tight font-medium">
                        {option}
                      </span>
                    </div>)}
              </div>
              <FormDescription className="font-sans text-xs text-muted-foreground">
                Select the services you'd like to receive maintenance tips and alerts for.
              </FormDescription>
              <FormMessage className="font-sans text-xs" />
            </FormItem>} />
      </div>

      {submitError && <div className="border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-sans text-sm">{submitError}</p>
        </div>}

      {}
      <Button type="submit" disabled={isSubmitting} variant="ghost" className="group w-full rounded-md bg-primary text-white px-4 py-2 font-medium font-sans text-sm uppercase tracking-widest transition-all duration-300 shadow-sm hover:shadow-lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            PROCESSING
          </> : <>
            SUBSCRIBE FOR ALERTS
            <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}